import { LegendList } from '@legendapp/list/react-native';
import capitalize from 'lodash.capitalize';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/Custom/Avatar';
import Box from '@/components/Unistyles/Box';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';
import { CLICK_OR_TAP } from '@/utils/constants';
import type {
  GeneratedGame,
  GeneratedGameTeam,
  GeneratedGameTeamSlotSelection,
} from './GeneratedGame.types';
import { getGeneratedGameTeamIds } from './GeneratedGame.utils';

type GeneratedGameRosterPanelProps = {
  games: readonly GeneratedGame[];
  onPressTeam: (teamId: string) => void;
  presentation?: RosterPresentation;
  selectedRosterTeamId?: string | null;
  selectedSlot?: GeneratedGameTeamSlotSelection | null;
  teams: readonly GeneratedGameTeam[];
};

type RosterPresentation = 'panel' | 'tray';

type TeamCounts = {
  away: number;
  home: number;
  total: number;
};

type GeneratedGameRosterTeamItem = {
  counts: TeamCounts;
  team: GeneratedGameTeam;
};

function getEmptyTeamCounts(): TeamCounts {
  return {
    away: 0,
    home: 0,
    total: 0,
  };
}

function getOrCreateTeamCounts(
  countsByTeamId: Map<string, TeamCounts>,
  teamId: string,
) {
  const counts = countsByTeamId.get(teamId);

  if (counts) {
    return counts;
  }

  const nextCounts = getEmptyTeamCounts();
  countsByTeamId.set(teamId, nextCounts);
  return nextCounts;
}

function getTeamCountsById({ games }: { games: readonly GeneratedGame[] }) {
  const countsByTeamId = new Map<string, TeamCounts>();

  for (const game of games) {
    const { awayTeamId, homeTeamId } = getGeneratedGameTeamIds(game);

    if (homeTeamId) {
      const homeCounts = getOrCreateTeamCounts(countsByTeamId, homeTeamId);
      homeCounts.home += 1;
      homeCounts.total += 1;
    }

    if (awayTeamId) {
      const awayCounts = getOrCreateTeamCounts(countsByTeamId, awayTeamId);
      awayCounts.away += 1;
      awayCounts.total += 1;
    }
  }

  return countsByTeamId;
}

function getRosterTeamItems({
  games,
  teams,
}: {
  games: readonly GeneratedGame[];
  teams: readonly GeneratedGameTeam[];
}) {
  const countsByTeamId = getTeamCountsById({ games });

  return teams.map((team) => ({
    counts: countsByTeamId.get(team.id) ?? getEmptyTeamCounts(),
    team,
  }));
}

function getGameLabel(count: number) {
  return count === 1 ? '1 game' : `${count} games`;
}

function getRosterTestID(presentation: RosterPresentation) {
  switch (presentation) {
    case 'panel':
      return 'GeneratedGameRosterPanel';
    case 'tray':
      return 'GeneratedGameRosterTray';
  }
}

function RosterPanelTeamSeparator() {
  return <Box style={styles.panelTeamSeparator} />;
}

export default function GeneratedGameRosterPanel({
  games,
  onPressTeam,
  presentation = 'panel',
  selectedRosterTeamId,
  selectedSlot,
  teams,
}: GeneratedGameRosterPanelProps) {
  const hasSelectedSlot = Boolean(selectedSlot);
  const isRosterTarget = hasSelectedSlot;
  const instruction = `${capitalize(CLICK_OR_TAP)} a Team to move it around.`;
  const rosterTeamItems = getRosterTeamItems({ games, teams });

  const listStateKey = [
    presentation,
    selectedRosterTeamId ?? '',
    selectedSlot?.gameId ?? '',
    selectedSlot?.slot ?? '',
    selectedSlot?.teamId ?? '',
  ].join(':');
  const rosterCountsKey = rosterTeamItems
    .map(({ counts, team }) =>
      [team.id, counts.total, counts.home, counts.away].join(':'),
    )
    .join('|');

  const renderItem = ({ item }: { item: GeneratedGameRosterTeamItem }) => {
    const { counts, team } = item;
    const selected = selectedRosterTeamId === team.id;
    const targeted = isRosterTarget && !selected;

    if (presentation === 'tray') {
      return (
        <Pressable
          accessibilityLabel={`Select ${team.title}`}
          accessibilityRole="button"
          accessibilityState={{ selected }}
          onPress={() => onPressTeam(team.id)}
          styleOverride={({ pressed }) =>
            styles.teamButton({ presentation, pressed, selected, targeted })
          }
          testID="GeneratedGameRosterTeamButton"
        >
          <Avatar
            color={team.home_primary_color}
            id={team.avatar_id ?? ''}
            name={team.title}
            size="lg"
          />
        </Pressable>
      );
    }

    return (
      <Pressable
        accessibilityLabel={`Select ${team.title}`}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        onPress={() => onPressTeam(team.id)}
        styleOverride={({ pressed }) =>
          styles.teamButton({ presentation, pressed, selected, targeted })
        }
        testID="GeneratedGameRosterTeamButton"
      >
        <Box direction="horizontal" alignItems="center" space="md">
          <Avatar
            color={team.home_primary_color}
            id={team.avatar_id ?? ''}
            name={team.title}
            size="md"
          />
          <Box flex space="xs">
            <Text bold numberOfLines={1}>
              {team.title}
            </Text>
            <Text size="xs" variant="typographySecondary" numberOfLines={1}>
              {getGameLabel(counts.total)} · {counts.home} home · {counts.away}{' '}
              away
            </Text>
          </Box>
        </Box>
      </Pressable>
    );
  };

  return (
    <Box
      style={styles.card(presentation)}
      testID={getRosterTestID(presentation)}
    >
      {presentation === 'panel' ? (
        <Box style={[styles.header, styles.panelHeader]} space="xs">
          <Text bold size="lg">
            Teams
          </Text>
          <Text size="sm" variant="typographySecondary">
            {instruction}
          </Text>
        </Box>
      ) : null}
      <LegendList
        ItemSeparatorComponent={
          presentation === 'panel' ? RosterPanelTeamSeparator : undefined
        }
        contentContainerStyle={styles.listContent(presentation)}
        data={rosterTeamItems}
        extraData={`${listStateKey}:${rosterCountsKey}`}
        horizontal={presentation !== 'panel'}
        keyExtractor={(item) => item.team.id}
        renderItem={renderItem}
        showsHorizontalScrollIndicator={false}
        style={styles.list(presentation)}
      />
    </Box>
  );
}

type RosterTeamButtonStyleOptions = {
  presentation: RosterPresentation;
  pressed: boolean;
  selected: boolean;
  targeted: boolean;
};

const styles = StyleSheet.create((theme) => ({
  card: (presentation: RosterPresentation) => ({
    alignSelf: 'stretch',
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius:
      presentation === 'tray' ? theme.borderRadius.full : theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    flex: presentation === 'panel' ? 1 : undefined,
    flexGrow: presentation === 'panel' ? 1 : 0,
    flexShrink: presentation === 'panel' ? 1 : 0,
    minHeight: presentation === 'tray' ? theme.size(16) : 0,
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  }),
  header: {
    borderBottomColor: theme.colors.borderMuted,
    borderBottomWidth: theme.size(0.5),
  },
  list: (presentation: RosterPresentation) => ({
    backgroundColor: theme.colors.surface,
    flex: presentation === 'panel' ? 1 : undefined,
    flexGrow: presentation === 'tray' ? 0 : undefined,
    flexShrink: presentation === 'tray' ? 0 : undefined,
    width: '100%',
  }),
  listContent: (presentation: RosterPresentation) => ({
    alignItems: presentation === 'tray' ? 'center' : undefined,
    backgroundColor: theme.colors.surface,
    padding: presentation === 'panel' ? theme.space('sm') : undefined,
    paddingHorizontal: presentation === 'tray' ? theme.space('md') : undefined,
    paddingVertical: presentation === 'tray' ? theme.space('sm') : undefined,
  }),
  panelHeader: {
    paddingHorizontal: theme.space('lg'),
    paddingVertical: theme.space('lg'),
  },
  panelTeamSeparator: {
    height: theme.space('sm'),
  },
  teamButton: ({
    presentation,
    pressed,
    selected,
    targeted,
  }: RosterTeamButtonStyleOptions) => ({
    alignItems: presentation === 'tray' ? 'center' : undefined,
    backgroundColor:
      selected || pressed ? theme.colors.surfaceSecondary : 'transparent',
    borderColor: selected || targeted ? theme.colors.secondary : 'transparent',
    borderCurve: 'continuous',
    borderRadius:
      presentation === 'tray' ? theme.borderRadius.full : theme.borderRadius.xl,
    borderStyle: targeted ? 'dotted' : 'solid',
    borderWidth: theme.size(0.5),
    justifyContent: presentation === 'tray' ? 'center' : undefined,
    marginRight: presentation === 'tray' ? theme.space('xs') : undefined,
    overflow: presentation === 'panel' ? 'hidden' : undefined,
    padding: presentation === 'tray' ? theme.space('sm') : undefined,
    paddingHorizontal: presentation === 'panel' ? theme.space('md') : undefined,
    paddingVertical: presentation === 'panel' ? theme.space('sm') : undefined,
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
      },
    },
  }),
}));
